import { supabase } from "@/integrations/supabase/client";
import type { Document } from "@/types/documents";
import type { DocumentCategoryRow } from "@/types/documents";
import { listDocumentCategories } from "@/stores/admin/repository";

/**
 * Extract category names from a document's categories array
 */
export function getCategoryNames(document: Document): string[] {
  if (document.categories && document.categories.length > 0) {
    const seen = new Set<string>();
    return document.categories
      .map((cat) => cat.name)
      .filter((name) => {
        const key = name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }
  // Fallback to subcategories for backward compatibility
  return document.subcategories || [];
}

/**
 * Find category ID by category name from a list of categories
 */
export function findCategoryId(
  categoryName: string,
  categories: DocumentCategoryRow[]
): number | null {
  const category = categories.find(
    (cat) => cat.category_name.toLowerCase() === categoryName.toLowerCase()
  );
  return category?.id ?? null;
}

/**
 * Sync categories to M2M table by replacing all existing categories
 * @param documentId - The document ID
 * @param categoryNames - Array of category names to set
 * @param userId - User ID for audit fields
 */
export async function syncCategoriesToM2M(
  documentId: number,
  categoryNames: string[],
  userId: number
): Promise<void> {
  // Fetch all available categories to map names to IDs
  const allCategories = await listDocumentCategories();

  // Map category names to IDs
  const categoryIds: number[] = [];
  for (const categoryName of categoryNames) {
    const categoryId = findCategoryId(categoryName, allCategories);
    if (categoryId !== null) {
      categoryIds.push(categoryId);
    } else {
      console.warn(`Category "${categoryName}" not found in document_categories table`);
    }
  }

  // Remove all existing categories for this document
  const { error: deleteError } = await supabase
    .from("document_document_categories")
    .delete()
    .eq("document_id", documentId);

  if (deleteError) {
    throw new Error(`Failed to remove existing categories: ${deleteError.message}`);
  }

  // Insert new categories if any
  if (categoryIds.length > 0) {
    const rows = categoryIds.map((categoryId) => ({
      document_id: documentId,
      category_id: categoryId,
      created_by: userId,
      modified_by: userId,
    }));

    const { error: insertError } = await supabase.from("document_document_categories").insert(rows);

    if (insertError) {
      throw new Error(`Failed to insert categories: ${insertError.message}`);
    }
  }
}

/**
 * Add a single category to a document
 * @param documentId - The document ID
 * @param categoryName - Category name to add
 * @param userId - User ID for audit fields
 */
export async function addCategoryToDocument(
  documentId: number,
  categoryName: string,
  userId: number
): Promise<void> {
  const allCategories = await listDocumentCategories();
  const categoryId = findCategoryId(categoryName, allCategories);

  if (categoryId === null) {
    throw new Error(`Category "${categoryName}" not found in document_categories table`);
  }

  // Check if category already exists for this document
  const { data: existing } = await supabase
    .from("document_document_categories")
    .select("category_id")
    .eq("document_id", documentId)
    .eq("category_id", categoryId)
    .single();

  if (existing) {
    // Category already exists, no need to add
    return;
  }

  // Insert the category
  const { error } = await supabase.from("document_document_categories").insert({
    document_id: documentId,
    category_id: categoryId,
    created_by: userId,
    modified_by: userId,
  });

  if (error) {
    throw new Error(`Failed to add category: ${error.message}`);
  }
}

/**
 * Remove a single category from a document
 * @param documentId - The document ID
 * @param categoryName - Category name to remove
 */
export async function removeCategoryFromDocument(
  documentId: number,
  categoryName: string
): Promise<void> {
  const allCategories = await listDocumentCategories();
  const categoryId = findCategoryId(categoryName, allCategories);

  if (categoryId === null) {
    throw new Error(`Category "${categoryName}" not found in document_categories table`);
  }

  const { error } = await supabase
    .from("document_document_categories")
    .delete()
    .eq("document_id", documentId)
    .eq("category_id", categoryId);

  if (error) {
    throw new Error(`Failed to remove category: ${error.message}`);
  }
}
